import { useState } from 'react';
import { dec } from '@app/shared';
import { App, Button, DatePicker, Flex, Input, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { INVALID_INPUTS_HINT, InvalidInputsContext, MoneyInput, useInvalidInputs } from '@/shared/ui/inputs';
import { CounterpartySelect } from '@/entities/counterparty';
import { useListDocumentsQuery } from '@/entities/document';
import type { PaymentDto } from '@/entities/payment';
import { useCreateOffsetMutation, useDeletePaymentMutation, useGetPaymentQuery, useUpdatePaymentMutation } from '@/entities/payment';
import { usePaymentSaveWithPreview, PaymentPreviewConsequencesDialog } from '@/features/payment/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { AllocationGrid, offsetIsBalanced, rowsFromDocuments, sumAmounts, toManualAllocations } from '@/features/payment/allocation-grid';
import type { AllocationRow } from '@/features/payment/allocation-grid';
import { useStoreContext } from '@/shared/lib/store-context';
import { formatDate, todayInTz } from '@/shared/lib/date';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface PaymentWindowProps {
  id?: string;
  counterpartyId?: string;
  onClose: () => void;
}

/** §7: взаимозачёт — единственный сценарий, оставшийся в этом окне после перехода оплат/возвратов
 * денег на кассовые ордера (ПКО/РКО, `widgets/cash-order-window`). Разнесение всегда ручное на
 * обеих сторонах — у взаимозачёта нет направления и нет авто-FIFO (`POST /payments/offset`
 * требует, чтобы обе стороны в сумме сходились с суммой взаимозачёта). */
export function PaymentWindow({ id, counterpartyId: initialCounterpartyId, onClose }: PaymentWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const isNew = !id;

  const {
    data: existing,
    isFetching,
    refetch: refetchExisting,
  } = useGetPaymentQuery(id ?? '', { skip: !id });
  const [createOffset, { isLoading: isCreatingOffset }] = useCreateOffsetMutation();
  const [updatePayment, { isLoading: isUpdating }] = useUpdatePaymentMutation();
  const [deletePayment, { isLoading: isDeleting }] = useDeletePaymentMutation();
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = usePaymentSaveWithPreview();

  const [counterpartyId, setCounterpartyId] = useState(initialCounterpartyId);
  const [date, setDate] = useState(todayInTz(ctx.timezone));
  const [amount, setAmount] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [receivableRows, setReceivableRows] = useState<AllocationRow[]>([]);
  const [payableRows, setPayableRows] = useState<AllocationRow[]>([]);

  function seedFrom(d: PaymentDto) {
    setCounterpartyId(d.counterpartyId);
    setDate(d.payDate.slice(0, 10));
    setAmount(d.amount);
    setComment(d.comment ?? '');
  }

  // Загруженный платёж → заполнить форму один раз (render-phase, не эффект).
  const [seededId, setSeededId] = useState<string | undefined>(undefined);
  if (existing && existing.id !== seededId) {
    setSeededId(existing.id);
    seedFrom(existing);
  }

  /** §8.7 «Параллельная правка»: VERSION_CONFLICT предлагает открыть свежую версию (отбрасывая
   * локальные правки) вместо тупикового тоста — тот же приём, что и в `DocumentWindow`. */
  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Взаимозачёт изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      onOk: async () => {
        const result = await refetchExisting();
        if (result.data) seedFrom(result.data);
      },
    });
  }

  const { data: receivableList } = useListDocumentsQuery(
    { type: 'SALE', counterpartyIds: counterpartyId ? [counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !counterpartyId },
  );
  const { data: payableList } = useListDocumentsQuery(
    { type: 'PURCHASE', counterpartyIds: counterpartyId ? [counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !counterpartyId },
  );

  const receivableKey = receivableList ? `r:${receivableList.items.map((i) => i.id).join(',')}` : undefined;
  const [seededReceivableKey, setSeededReceivableKey] = useState<string | undefined>(undefined);
  if (receivableKey && receivableKey !== seededReceivableKey) {
    setSeededReceivableKey(receivableKey);
    setReceivableRows(rowsFromDocuments(receivableList!.items, 'С'));
  }
  const payableKey = payableList ? `p:${payableList.items.map((i) => i.id).join(',')}` : undefined;
  const [seededPayableKey, setSeededPayableKey] = useState<string | undefined>(undefined);
  if (payableKey && payableKey !== seededPayableKey) {
    setSeededPayableKey(payableKey);
    setPayableRows(rowsFromDocuments(payableList!.items, 'П'));
  }

  const busy = isCreatingOffset || isUpdating || isDeleting || isRunningPreview;
  // Сумма с опечаткой в строке разнесения уходит из поля как «пусто» — платёж сохранился бы с
  // автоматическим разнесением вместо введённого вручную.
  const invalidInputs = useInvalidInputs();
  const canSubmitOffset = offsetIsBalanced(amount ?? '', receivableRows, payableRows);
  const canSubmit = !invalidInputs.hasInvalid && !!counterpartyId && !!date && !!amount && dec(amount).gt(0) && canSubmitOffset;

  async function handleCreate() {
    if (!counterpartyId || !amount || invalidInputs.hasInvalid) return;
    try {
      await createOffset({
        counterpartyId,
        date,
        amount,
        comment: comment || undefined,
        receivable: toManualAllocations(receivableRows),
        payable: toManualAllocations(payableRows),
      }).unwrap();
      message.success('Взаимозачёт создан');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleSaveFields() {
    if (!id || !existing || invalidInputs.hasInvalid) return;
    const fields = { date, amount: amount ?? undefined, comment };
    try {
      const saved = await runWithPreview(id, 'update', existing.rowVersion, () => updatePayment({ id, body: { rowVersion: existing.rowVersion, ...fields } }).unwrap(), fields);
      if (saved) message.success('Сохранено');
    } catch (e) {
      handleMutationError(e);
    }
  }

  function handleDelete() {
    if (!id || !existing) return;
    modal.confirm({
      title: 'Удалить взаимозачёт?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const deleted = await runWithPreview(id, 'remove', existing.rowVersion, () => deletePayment({ id, body: { rowVersion: existing.rowVersion } }).unwrap());
          if (deleted) {
            message.success('Взаимозачёт удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  return (
    <>
      <PaymentPreviewConsequencesDialog diff={pendingDiff} onResolve={resolvePending} />
      <FloatingWindow kind="payment" title={isNew ? 'Новый взаимозачёт' : `Взаимозачёт от ${formatDate(date)}`} onClose={onClose} footer={renderFooter()}>
        <InvalidInputsContext.Provider value={invalidInputs.api}>
          <Flex vertical gap={8}>
            <Flex gap={8} wrap>
              <Flex vertical style={{ minWidth: 220, flex: 1 }}>
                <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
                <CounterpartySelect value={counterpartyId} current={existing?.counterparty} disabled={!isNew} onChange={setCounterpartyId} />
              </Flex>
              <Flex vertical style={{ minWidth: 140 }}>
                <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
                <DatePicker value={dayjs(date)} format="DD.MM.YYYY" onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : date)} />
              </Flex>
            </Flex>

            <Flex gap={8} wrap>
              <Flex vertical style={{ minWidth: 140 }}>
                <Typography.Text style={{ fontSize: 12 }}>Сумма</Typography.Text>
                <MoneyInput value={amount} onChange={setAmount} />
              </Flex>
              <Flex vertical style={{ minWidth: 200, flex: 1 }}>
                <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
                <Input value={comment} onChange={(e) => setComment(e.target.value)} />
              </Flex>
            </Flex>

            {counterpartyId ? (
              <Flex gap={12} wrap>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <AllocationGrid title={`Нам должны (${sumAmounts(receivableRows)})`} rows={receivableRows} onChange={setReceivableRows} />
                </div>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <AllocationGrid title={`Мы должны (${sumAmounts(payableRows)})`} rows={payableRows} onChange={setPayableRows} />
                </div>
              </Flex>
            ) : null}

            {invalidInputs.hasInvalid ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {INVALID_INPUTS_HINT}
              </Typography.Text>
            ) : null}
          </Flex>
        </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  function renderFooter() {
    if (isFetching && id) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;
    if (isNew) {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canSubmit} onClick={() => void handleCreate()}>
            Добавить
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      );
    }
    return (
      <Space wrap>
        <Button type="primary" loading={busy} disabled={invalidInputs.hasInvalid || !amount || dec(amount).lte(0)} onClick={() => void handleSaveFields()}>
          Сохранить
        </Button>
        <Button danger loading={busy} onClick={handleDelete}>
          Удалить
        </Button>
        <Button onClick={onClose}>Закрыть</Button>
      </Space>
    );
  }
}
